import { useProperty, useTranslation } from '@eti/providers';
import { breakpoints, Button, Stack } from '@etg/wings';
import { css } from '@eti/styles';
import { forwardRef } from 'react';
import { useFormContext, type FieldErrors } from 'react-hook-form';
import type { SearchFormValues } from '../../../common/search-form-panel/SearchFormContainer';
import DirectFlightCheckBox from './DirectFlightCheckBox';
import ZeroWidthSpace from './ZeroWidthSpace';

const getButtonWrapperStyles = (maxHeight: string) => css`
  max-height: ${maxHeight};
  place-content: space-between;
  transition: max-height 0.2s;

  @media (min-width: ${breakpoints._768}) {
    flex-direction: column-reverse;
  }
`;

const buttonStyles = css`
  width: 100%;

  @media (min-width: ${breakpoints._970}) {
    min-width: 180px;
  }
`;

const getWrapperMaxHeight = (errors: FieldErrors<SearchFormValues>) => {
  const { origin, destination, departureDate, returnDate } = errors?.singleBound || {};
  const hasLocationError = origin || destination;
  const hasDateError = departureDate || returnDate;

  if (hasLocationError) {
    return '190px';
  }
  if (hasDateError) {
    return '165px';
  }
  return 'fit-content';
};

type SubmitButtonProps = {
  areOriginAndDestinationFilled?: boolean;
  isMultiStop?: boolean;
};

const SubmitButton = forwardRef<HTMLAnchorElement, SubmitButtonProps>(
  ({ areOriginAndDestinationFilled, isMultiStop }, ref) => {
    const { t } = useTranslation();
    const { p } = useProperty();
    const {
      formState: { errors },
    } = useFormContext<SearchFormValues>();
    const maxWrapperHeight = getWrapperMaxHeight(errors);

    const showDirectFlight =
      !p('SearchForm.ShowExtendedForm.Enabled') &&
      !isMultiStop &&
      areOriginAndDestinationFilled &&
      p('SearchForm.DirectFlights.Checkbox.Enabled');

    const redirectHref: string = p('SearchForm.RedirectHref.Value');
    const isLink = Boolean(p('SearchForm.RedirectHref.Enabled')) && Boolean(redirectHref);

    return (
      <Stack className={getButtonWrapperStyles(maxWrapperHeight)} spacing={24}>
        {showDirectFlight && (
          <Stack spacing={4}>
            <ZeroWidthSpace isDesktopOnly />
            <DirectFlightCheckBox />
          </Stack>
        )}
        <Stack spacing={4}>
          <ZeroWidthSpace isDesktopOnly />
          <Button
            as={isLink ? 'a' : undefined}
            className={buttonStyles}
            data-testid={
              isLink ? 'searchForm-searchFlights-link' : 'searchForm-searchFlights-button'
            }
            href={isLink ? redirectHref : undefined}
            ref={ref}
            type={isLink ? undefined : 'submit'}
            variant="primary"
          >
            {t('Button.Text.Search.Air')}
          </Button>
        </Stack>
      </Stack>
    );
  },
);

export default SubmitButton;
